<script lang="ts">
    import { cn } from '$lib/utils/cn';

    import { afterUpdate, tick } from 'svelte';
    import clickOutside from 'svelte-outside-click';

    import StatusIcon from './StatusIcon.svelte';
    import Emoji from '../../_wrappers/Emoji.svelte';
    import { resolveInputStyling } from '../_utils/resolveInputStyling';
    import {
        dateInputsEmpty,
        formatDateMonthInput,
        formatYear,
        fullDateEntered,
        getDateFormatObject,
        validateDateInput,
    } from '../helper';

    import type { Theme } from '$lib/types/Theme';

    export let placeholder = '';
    export let customOptions = {
        dateFormat: '',
    };
    export let showEmoji = false;
    export let emoji = '';
    export let required = false;
    export let pristine = true;
    export let touched = false;
    export let finalFormInput: { name: string; onChange: (value: string) => void } = {
        name: '',
        onChange: () => {},
    };
    export let errorMessage = '';
    export let focusColor = '';
    export let outlineColor = '';
    export let theme: Theme | undefined = undefined;

    // Custom meta
    let invalid = false;

    let showError = invalid && touched;
    let showSuccess = !invalid && touched && !pristine;
    let showCheckmark = true;

    // Input values
    let inputValues = {
        day: '',
        month: '',
        year: '',
    };

    let prevValues = {
        day: '',
        month: '',
        year: '',
    };

    const dateFormatObject = getDateFormatObject(customOptions.dateFormat);
    let editing = false;

    let globallyFocused = false; // Aids global input styling
    let skipGlobalFocus = false; // Aids Tab travel between fields
    let skipFocusCatcher = false; // Aids Shift + Tab travel between fields
    let highlightSeparator = false; // Aids separator styling

    // Refs
    let globalInputRef: HTMLInputElement;
    const orderedRefs: Record<number, HTMLInputElement | undefined> = {
        [dateFormatObject.day]: undefined,
        [dateFormatObject.month]: undefined,
        [dateFormatObject.year]: undefined,
    };

    const handleValidDateEntered = (dateSelected: Date) => {
        const dateTimeStamp = dateSelected.toISOString();
        globalInputRef.value = dateTimeStamp;
        finalFormInput.onChange(dateTimeStamp);
    };

    const handleEmptyDateEntered = () => {
        if (globalInputRef.value !== '') {
            globalInputRef.value = '';
            finalFormInput.onChange('');
        }
    };

    const handleInvalidDateEntered = () => {
        if (globalInputRef.value !== 'invalid') {
            globalInputRef.value = 'invalid';
            finalFormInput.onChange('invalid');
        }
    };

    afterUpdate(() => {
        let dateSelected;
        let isFullDateEntered = fullDateEntered(inputValues);
        let areDateInputsEmpty = dateInputsEmpty(inputValues);

        highlightSeparator = !areDateInputsEmpty;

        // Reset validation on empty
        if (areDateInputsEmpty) {
            invalid = required && pristine && touched;
        }

        // Validate input if full date has been entered
        if (isFullDateEntered) {
            invalid = validateDateInput(inputValues, touched);

            const dateSelectedUTCTimestamp = !invalid
                ? Date.UTC(
                      Number(inputValues.year),
                      Number(inputValues.month) - 1,
                      Number(inputValues.day),
                  )
                : undefined;

            dateSelected = dateSelectedUTCTimestamp
                ? new Date(dateSelectedUTCTimestamp)
                : undefined;
        } else if (!areDateInputsEmpty) {
            invalid = true;
        }

        // Assign value to global input if date is valid
        if (!invalid && dateSelected && !isNaN(dateSelected.getTime())) {
            handleValidDateEntered(dateSelected);
        } else if (areDateInputsEmpty) {
            handleEmptyDateEntered();
        } else if (invalid) {
            handleInvalidDateEntered();
        }

        showError =
            (invalid && (touched || isFullDateEntered)) || (required && pristine && touched);
        showSuccess = !invalid && !pristine && isFullDateEntered;
        showCheckmark = true;
    });

    const handleBlur = (
        evt: FocusEvent & { currentTarget: EventTarget & HTMLInputElement },
        inputField: 'day' | 'month' | 'year',
        formatInput: boolean,
    ) => {
        skipGlobalFocus = false;
        skipFocusCatcher = false;

        // Handle single digit in day/month
        if (formatInput && evt.currentTarget.value.length === 1) {
            inputValues[inputField] = `0${evt.currentTarget.value}`;
        }
    };

    const handleClick = (evt: MouseEvent) => {
        evt.stopPropagation();
    };

    const handleFocus = () => {
        skipFocusCatcher = true;
    };

    const handleGlobalClick = async () => {
        enableEditing();
        await tick();
        focusOnInput(1, false);
    };

    const enableEditing = () => {
        editing = true;
        globallyFocused = true;
    };

    const handleKeyDown = (
        event: KeyboardEvent & { currentTarget: EventTarget & HTMLInputElement },
        inputOrder: number,
    ) => {
        const travelBack = event.shiftKey && event.key === 'Tab';
        const canTravel = travelBack ? inputOrder > 1 : inputOrder < 3;
        const inputHighlighted =
            event.currentTarget.selectionStart !== event.currentTarget.selectionEnd;
        const caretAtInputStart = event.currentTarget.selectionStart === 0;
        const caretAtInputEnd =
            event.currentTarget.selectionStart ===
            Number(event.currentTarget.getAttribute('maxlength'));

        // Handle Tab travel
        if (event.key === 'Tab' && canTravel) {
            event.preventDefault();
            focusNextInput(inputOrder, travelBack, true);
        } else if (travelBack && !canTravel) {
            skipGlobalFocus = true;
        }

        // Handle focused state
        if (event.key === 'Tab' && !canTravel) {
            globallyFocused = false;
            closeIfEmpty();
        }

        // Handle Backspace
        if (
            event.key === 'Backspace' &&
            !inputHighlighted &&
            (!event.currentTarget.value.length || caretAtInputStart)
        ) {
            focusNextInput(inputOrder, true, false, true);
        }

        // Handle Arrow travel
        if (event.key === 'ArrowLeft' && caretAtInputStart) {
            event.preventDefault();
            focusNextInput(inputOrder, true);
        } else if (event.key === 'ArrowRight' && caretAtInputEnd) {
            event.preventDefault();
            focusNextInput(inputOrder);
        }
    };

    const handleGlobalFocus = async () => {
        if (skipGlobalFocus) {
            skipGlobalFocus = false;
            return;
        }

        enableEditing();
        await tick();
        focusOnInput(1, true);
    };

    const handleFocusCatcher = async () => {
        enableEditing();
        await tick();
        focusOnInput(3, !!orderedRefs[3]?.value);
    };

    const closeIfEmpty = () => {
        handleTouched();

        if (dateInputsEmpty(inputValues)) {
            // Defer so an in-flight Tab can finish moving focus before
            // the segment inputs unmount.
            setTimeout(() => {
                editing = false;
                invalid = required && touched;
            });
        } else {
            // Validate on global blur
            invalid = validateDateInput(inputValues, touched);
        }
    };

    const handleClickOutside = () => {
        if (!editing) return;

        closeIfEmpty();
        globallyFocused = false;
    };

    const focusNextInput = (
        inputOrder: number,
        descending = false,
        selectInput = false,
        startAtInputEnd = false,
    ) => {
        const nextInputOrder = descending ? inputOrder - 1 : inputOrder + 1;
        focusOnInput(nextInputOrder, selectInput, startAtInputEnd);
    };

    const focusOnInput = (order: number, selectInput: boolean, startAtInputEnd?: boolean) => {
        const inputToFocus = orderedRefs[order];

        if (inputToFocus) {
            inputToFocus.focus();

            if (selectInput) {
                if (inputToFocus.value) {
                    inputToFocus.select();
                }
            } else if (startAtInputEnd) {
                const maxLength = Number(inputToFocus.getAttribute('maxlength'));
                inputToFocus.selectionStart = maxLength;
            }
        }
    };

    const handlePristine = () => {
        pristine = !!dateInputsEmpty(inputValues);
    };

    const handleTouched = () => {
        if (!touched) {
            touched = true;
        }
    };

    const handleMeta = () => {
        handlePristine();
    };

    const handleInputEnd = (fieldOrder: number, inputLength: number, maxLength: number) => {
        // Focus on next input if field is full
        if (inputLength === maxLength) {
            focusNextInput(fieldOrder, false, true);
        }
    };

    const validateDateMonth = (inputField: 'day' | 'month') => {
        const formattedInput = formatDateMonthInput(
            inputField,
            inputValues[inputField],
            prevValues[inputField],
        );

        inputValues[inputField] = formattedInput;
        prevValues[inputField] = formattedInput;

        handleInputEnd(dateFormatObject[inputField], formattedInput.length, 2);
        handleMeta();
    };

    const validateYear = () => {
        const formattedInput = formatYear(inputValues.year, prevValues['year']);
        inputValues.year = formattedInput;
        prevValues.year = formattedInput;

        handleInputEnd(dateFormatObject.year, formattedInput.length, 4);
        handleMeta();
    };

    const {
        borderRadiusClass,
        shadowClass,
        borderColor,
        backgroundColor,
        textColor,
        mutedTextColor,
    } = resolveInputStyling({
        theme,
        shadowComponent: 'textInput',
    });

    $: inputWrapperClasses = cn(
        borderRadiusClass,
        shadowClass,
        'fun-box-border fun-flex fun-w-full fun-cursor-text fun-flex-row fun-border fun-bg-white fun-px-4 fun-font-ui fun-text-base fun-font-light fun-ring-2 fun-ring-transparent fun-transition-all',
        {
            ['fun-pl-12']: showEmoji,
            ['fun-h-20']: invalid && showError,
            ['fun-h-14']: !invalid || !showError,
            ['fun-pr-24']: !required && pristine,
            ['fun-border-[var(--focusColor)] fun-ring-[var(--outlineColor)]']: globallyFocused,
        },
    );

    const getInputClasses = (order: number, isYear?: boolean) =>
        cn(
            'fun-z-10 fun-bg-transparent fun-text-left fun-font-ui fun-text-base fun-font-light fun-shadow-none fun-transition placeholder:fun-font-mono placeholder:fun-font-light focus:fun-outline-none',
            !mutedTextColor && 'fun-text-black placeholder:fun-text-gray-500',
            {
                'fun-w-10': isYear,
                'fun-w-5': !isYear,
                'fun-order-1 fun-ml-0': order === 1,
                'fun-order-3': order === 2,
                'fun-order-5 fun-mr-0': order === 3,
            },
        );

    const placeholderClasses = cn(
        'fun-z-20 fun-mt-3.5 fun-truncate fun-text-base fun-font-light',
        !mutedTextColor && 'fun-text-gray-500',
    );

    const getSeparatorClasses = (order: number) =>
        cn('fun-font-light', !mutedTextColor && 'fun-text-gray-500', {
            'fun-text-black': !mutedTextColor && highlightSeparator && !invalid,
            'fun-text-gray-300': !highlightSeparator && !invalid,
            'fun-order-2': order === 1,
            'fun-order-4': order === 2,
        });

    const dateInputClasses = cn(
        'fun-absolute fun-bottom-0 fun-top-0 fun-mt-[15px] fun-flex fun-h-fit fun-flex-row',
        {
            'fun-left-4': !showEmoji,
            'fun-left-12': showEmoji,
        },
    );
</script>

<div class="fun-relative fun-box-border fun-flex">
    {#if showEmoji}
        <u
            class="fun-absolute fun-left-4 fun-flex fun-h-14 fun-select-none fun-items-center fun-no-underline"
            aria-hidden="true"
        >
            <Emoji {emoji} size="20px" />
        </u>
    {/if}

    {#if !required && !invalid && pristine}
        <div
            class="fun-absolute fun-right-6 fun-flex fun-h-full fun-items-center fun-text-xs fun-font-light fun-text-gray-500 fun-no-underline"
        >
            <span>(optional)</span>
        </div>
    {/if}

    <StatusIcon {showCheckmark} {showError} {showSuccess} />

    <div
        class={inputWrapperClasses}
        on:click={handleGlobalClick}
        use:clickOutside={handleClickOutside}
        style:background-color={backgroundColor}
        style:border-color={!globallyFocused ? borderColor : undefined}
        style="--outlineColor: {outlineColor}; --focusColor: {focusColor}"
        role="presentation"
    >
        <input
            type="text"
            name={finalFormInput.name}
            class="fun-absolute fun--z-10 fun-opacity-0"
            bind:this={globalInputRef}
            on:focus={handleGlobalFocus}
            tabindex={skipGlobalFocus ? -1 : 0}
            aria-label={placeholder}
        />
        {#if editing}
            <div class={dateInputClasses}>
                <input
                    id="day"
                    type="text"
                    pattern="\d*"
                    class={getInputClasses(dateFormatObject.day)}
                    class:themed-placeholder={!!mutedTextColor}
                    style:color={textColor}
                    style:--placeholder-color={mutedTextColor}
                    placeholder="dd"
                    maxlength="2"
                    tabindex="-1"
                    bind:this={orderedRefs[dateFormatObject.day]}
                    bind:value={inputValues.day}
                    on:blur={(e) => handleBlur(e, 'day', true)}
                    on:focus={handleFocus}
                    on:input={() => validateDateMonth('day')}
                    on:keydown={(e) => handleKeyDown(e, dateFormatObject.day)}
                    on:click={handleClick}
                />
                <span class={getSeparatorClasses(1)} style:color={textColor}
                    >{dateFormatObject.separator}</span
                >
                <input
                    id="month"
                    type="text"
                    pattern="\d*"
                    class={`${getInputClasses(dateFormatObject.month)}`}
                    class:themed-placeholder={!!mutedTextColor}
                    style:color={textColor}
                    style:--placeholder-color={mutedTextColor}
                    placeholder="mm"
                    maxlength="2"
                    tabindex="-1"
                    bind:this={orderedRefs[dateFormatObject.month]}
                    bind:value={inputValues.month}
                    on:blur={(e) => handleBlur(e, 'month', true)}
                    on:focus={handleFocus}
                    on:input={() => validateDateMonth('month')}
                    on:keydown={(e) => handleKeyDown(e, dateFormatObject.month)}
                    on:click={handleClick}
                />
                <span class={getSeparatorClasses(2)} style:color={textColor}
                    >{dateFormatObject.separator}</span
                >
                <input
                    id="year"
                    type="text"
                    pattern="\d*"
                    class={`${getInputClasses(dateFormatObject.year, true)}`}
                    class:themed-placeholder={!!mutedTextColor}
                    style:color={textColor}
                    style:--placeholder-color={mutedTextColor}
                    placeholder="yyyy"
                    maxlength="4"
                    tabindex="-1"
                    bind:this={orderedRefs[dateFormatObject.year]}
                    bind:value={inputValues.year}
                    on:blur={(e) => handleBlur(e, 'year', false)}
                    on:focus={handleFocus}
                    on:input={validateYear}
                    on:keydown={(e) => handleKeyDown(e, dateFormatObject.year)}
                    on:click={handleClick}
                />
            </div>
        {:else}
            <p class={placeholderClasses} style:color={mutedTextColor}>
                {placeholder}
            </p>
        {/if}

        {#if errorMessage && showError}
            <div
                class="fun-absolute fun-top-12 fun-text-xs fun-font-normal fun-leading-tight fun-text-red-600"
            >
                {errorMessage}
            </div>
        {/if}

        <input
            type="text"
            class="fun-absolute fun--z-20 fun-opacity-0"
            on:focus={handleFocusCatcher}
            tabindex={skipFocusCatcher ? -1 : 0}
            aria-label={placeholder}
        />
    </div>
</div>

<style>
    input.themed-placeholder::placeholder {
        color: var(--placeholder-color);
    }
</style>
